import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Store, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirm) { setError("Passwords do not match"); return; }
    if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword, confirm_password: confirm });
      setToken(null);
      toast.success("Password reset successfully. Please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-extrabold text-lg font-outfit">S</div>
            <div>
              <div className="font-extrabold font-outfit text-lg leading-tight">Sonu Ki Dukaan</div>
              <div className="text-[11px] text-muted-foreground font-mono">Admin Panel</div>
            </div>
          </div>
          {!token ? (
            <div data-testid="reset-invalid-message">
              <h1 className="text-2xl font-extrabold font-outfit tracking-tight">Invalid link</h1>
              <p className="text-sm text-muted-foreground mt-2">This password reset link is missing or invalid. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-sm font-medium text-primary hover:underline" data-testid="request-new-link">Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <h1 className="text-2xl font-extrabold font-outfit tracking-tight">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Choose a strong password you haven’t used before.</p>
              {error && (
                <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="reset-error">{error}</div>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="new">New password</Label>
                  <Input id="new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••" autoComplete="new-password" required className="mt-1.5" data-testid="reset-new-password-input" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••" autoComplete="new-password" required className="mt-1.5" data-testid="reset-confirm-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</> : "Reset password"}
                </Button>
                <Link to="/login" className="block text-center text-sm font-medium text-primary hover:underline" data-testid="back-to-login-link">← Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-10 relative overflow-hidden">
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white/15">
            <MapPin className="h-3 w-3" /> Operating in Lucknow, UP
          </span>
        </div>
        <div className="relative z-10">
          <Store className="h-10 w-10 mb-4 opacity-90" />
          <h2 className="text-3xl font-extrabold font-outfit leading-tight">Bachat Ki Dukaan</h2>
        </div>
        <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-white/10" />
      </div>
    </div>
  );
}
